import { AccountTypes } from '../../models/Common';
import { TypeOfSocials } from '../../models/Socials';

const USER_INFO = {
  INFO: '/user/info',
  BANNERS: '/user/banners',
  REPAIR_WORKS: '/user/repairsInfo/progress',
  PUSHS_SETTINGS: '/user/push/options',
  CONNECTED_SERVICES: '/user/accounts',
  DOCUMENTS: '/user/documents',
  CREATE_DOCUMENT: '/user/document/termination',
  DOWNLOAD_DOCUMENT: '/user/document/download',
  SHOW_DOCUMENT: '/user/document/show',
  GET_APP_SETTINGS: '/software/settings',
  SET_NEW_ACCOUNT_PASSWORD: '/v2/user/password-update',
  SET_EMAIL_NOTIFICATION: '/user/email/options',
  SET_SMS_NOTIFICATION: '/user/sms/settings',
  ADD_OR_EDIT_EMAIL: '/v2/user/email',
  ADD_EMAIL: '/user/email',
  DELETE_EMAIL: '/v2/user/email',
  CONFIRM_EMAIL: '/user/email/confirm',
  RESEND_EMAIL: '/user/email/resend',
  CHANGE_BACKUP_PHONE: '/user/phones',
  ADD_SMS_PHONE: '/user/sms',
  EDIT_SMS_PHONE: '/v2/user/sms',
  REQUEST_CONFIRMATION_SMS_CODE_NEW: '/user/sms/confirm/send',
  REQUEST_CONFIRMATION_SMS_CODE_NEW_EDIT: '/v2/user/sms/confirm/send',
  CHECK_CONFIRMATION_SMS_CODE: '/user/sms/confirm/check',
  CHANGE_USER_SERVICE_LANGUAGE: '/user/language',
  MULTIACCOUNT_MEMBERS: '/user/multiaccount/members',
  MULTIACCOUNT_JOIN: '/user/multiaccount/join',
  RECOMMENDED_TOP_UP_AMOUNT: '/user/payments/recommendedTopUpAmount',
  GET_AUTH_TOKEN_BRIZ_TV: '/user/brizTv/temporaryAuthToken',
};

const PAYMENTS = {
  CALCULATION_COST_OF_IPTV: (accountId: string | number, accountType: string) => {
    return `/user/${accountType}/${accountId}/calculate`;
  },
  CREATE_SERVICE_ORDER: (accountId: string | number, accountType: string) => {
    return `/user/${accountType}/${accountId}/order`;
  },
  PAYMENT: (accountId: string | number, accountType: string) => {
    return `/user/${accountType}/${accountId}/pay`;
  },
  GET_SERVICE_PRESETS: (accountId: string | number, accountType: AccountTypes | string) => {
    return `/user/${accountType}/${accountId}/init?transform=false`;
  },
  GETTING_PRESETS_FOR_ALL_SERVICES: '/user/payments/init',
  PAYMENT_STATISTICS: '/user/payments/history',
  PAYMENT_BONUSES: '/user/payments/history/bonus',
  PAYMENT_GOOGLE_PAY: '/user/payments/platon/googlePay',
  GETTING_ORDERS_INFO: (orderCode: string) => {
    return `/user/payments/orders${orderCode ? `/${orderCode}` : ''}`;
  },
};

const REGULAR_PAYMENTS = {
  DISABLE: '/user/payments/regular/disable',
  ENABLE: '/user/payments/regular/enable',
  GET_SETTINGS: '/v2/user/payments/regular',
  CHANGE_EMAIL: 'user/payments/regular/email',
  SWITCH_REGULAR_PAYMENT: 'user/payments/regular',
  SET_PAYMENT_CARD: 'user/payments/regular/card',
};

const PAYMENTS_CARDS = {
  LIST_OF_CARDS: '/user/payments/cards',
  DELETE_CARD: (cardId: string) => {
    return `/user/payments/cards/${cardId}`;
  },
};

const INTERNET = {
  ACCOUNT_INFO: (accountId: string | number) => {
    return `/user/internet/${accountId}`;
  },
  SERVICES_AVAILABLE: (accountId: string | number) => {
    return `/user/internet/${accountId}/services/available`;
  },
  UPDATE_PASSWORD_OR_LOGIN: (accountId: number | string) => {
    return `/v2/user/internet/${accountId}/credentials`;
  },
  CHANGE_MAC: (accountId: number | string) => {
    return `/v2/user/internet/${accountId}/mac`;
  },
  LIST_OF_SESSIONS: (accountId: string | number) => {
    return `/user/internet/${accountId}/sessions`;
  },
  CURRENT_SESSION: (accountId: string | number) => {
    return `/user/internet/${accountId}/online-sessions`;
  },
};

const IPTV = {
  ACCOUNT_INFO: (accountId: string | number) => {
    return `/user/iptv/${accountId}`;
  },
  SERVICES_AVAILABLE: (accountId: string | number) => {
    return `/user/iptv/${accountId}/services/available`;
  },
  DELETE_OR_EDIT_DEVICE: (accountId: string | number, deviceId: number) => {
    return `/user/iptv/${accountId}/device/${deviceId}`;
  },
  ADD_DEVICE: (accountId: string | number) => {
    return `/user/iptv/${accountId}/device`;
  },
  BRIZ_TV: (accountId?: string | number) => {
    return `/v2/user/iptv/${accountId}/ott`;
  },
  BRIZ_TV_OLD: (accountId?: string | number) => {
    return `/user/iptv/${accountId}/ott`;
  },
  CONNECT_SHARE: (accountId: string, shareId: string) => {
    return `/user/iptv/${accountId}/share/${shareId}/activate`;
  },
};

const CTV = {
  ACCOUNT_INFO: (accountId: string | number) => {
    return `/user/ctv/${accountId}`;
  },
  SERVICES_AVAILABLE: (accountId: string | number) => {
    return `/user/ctv/${accountId}/services/available`;
  },
};

const ORDERS = {
  CALLBACK: '/user/orders/callback',
  UNAUTHORIZED_CALLBACK: '/orders/callback',
  FEEDBACK: '/orders/feedback',
};

const UNION = {
  ACCOUNT_INFO: (accountId: string | number) => {
    return `/user/union/${accountId}`;
  },
  SERVICES_AVAILABLE: (accountId: string | number) => {
    return `/user/union/${accountId}/services/available`;
  },
};

const AUTHORIZATION = {
  AUTH: '/auth',
  LOGOUT: '/logout',
  PASSWORD_RESET: '/reset',
  PASSWORD_RESET_V2: '/v2/reset',
  EMAIL_RESET_CALLBACK: '/app/login/password-recovery-by-email?step=1',
  EMAIL_RESET_CALLBACK_IS_AUTH: '/app/login/password-recovery-by-email?step=1',
  PASSWORD_CONFIRM: '/password-confirm',
  PASSWORD_CONFIRM_V2: '/v2/password-confirm',
};

const TOP_UP = {
  SCRATCH_CARD: '/user/money/card/scratch',
  PLATON_FORM: '/user/payments/platon/invoice',
};

const SOCIALS = {
  UNLINK: '/user/socials/disable',
  CONNECT: (social: TypeOfSocials) => {
    return `/user/socials/${social}`;
  },
};

const FIREBASE = {
  CONNECT_DEVICE: '/firebase/connectDevice',
};

const RE_CAPTCHA = {
  SiteVerify: '/siteverify',
};

const PUSH_NOTIFICATIONS = {
  GET_NOTIFICATIONS: '/firebase/messages',
  MARK_NOTIFICATIONS_AS_READ: 'firebase/read',
};

const BRIZ = {
  NEWS: '/api/news',
  SHARES: '/api/shares',
};

const SURVEY = {
  FORM: (id: string) => `/survey/${id}`,
};

export const config = {
  USER_INFO,
  AUTHORIZATION,
  TOP_UP,
  SOCIALS,
  RE_CAPTCHA,
  IPTV,
  CTV,
  PUSH_NOTIFICATIONS,
  PAYMENTS,
  REGULAR_PAYMENTS,
  PAYMENTS_CARDS,
  SURVEY,
  INTERNET,
  UNION,
  ORDERS,
  BRIZ,
  FIREBASE,
};
